import React, { useState, useEffect, useRef } from "react";
import { SpecialBarWrapper, SpecialBarContent, SpecialBarTitle, SpecialBarDescription, SpecialBarCTA, CloseButton } from "./index.styled";

const SpecialBar = () => {
  const [specialData, setSpecialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const barRef = useRef(null);

  useEffect(() => {
    const fetchSpecialData = async () => {
      try {
        const response = await fetch(
          `${process.env.GATSBY_PUBLIC_WP_API}/wp-json/specials/v1/settings` 
        );
        const data = await response.json();
        
        if (data.success && data.data?.top_bar_specials?.enabled) {
          setSpecialData(data.data.top_bar_specials);
        }
      } catch (error) {
        console.error("Error fetching special bar data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialData();

    return () => {
      document.body.classList.remove('has-special-bar');
    };
  }, []);

  useEffect(() => {
    if (!isDismissed && !isClosing && specialData) {
      document.body.classList.add('has-special-bar');
    } else {
      document.body.classList.remove('has-special-bar');
      document.documentElement.style.setProperty('--special-bar-height', '0px');
    }
  }, [isDismissed, isClosing, specialData]);

  useEffect(() => {
    if (barRef.current && specialData && !isDismissed && !isClosing) {
      const updateHeight = () => {
        const height = barRef.current.offsetHeight;
        document.documentElement.style.setProperty('--special-bar-height', `${height}px`);
      };

      // Small delay to ensure the element is rendered before calculating height
      setTimeout(updateHeight, 100);
      window.addEventListener('resize', updateHeight);

      return () => {
        window.removeEventListener('resize', updateHeight);
      };
    }
  }, [specialData, isDismissed, isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    document.body.classList.remove('has-special-bar');
    
    // Wait for animation to complete before removing from DOM
    setTimeout(() => {
      setIsDismissed(true);
      document.documentElement.style.setProperty('--special-bar-height', '0px');
    }, 400); // Match animation duration
  };

  if (loading || !specialData || isDismissed) {
    return null;
  }

  return (
    <SpecialBarWrapper ref={barRef} $isClosing={isClosing}>
      <SpecialBarContent>
        {specialData.title && (
          <SpecialBarTitle>{specialData.title}</SpecialBarTitle>
        )}
        {specialData.description && (
          <SpecialBarDescription>{specialData.description}</SpecialBarDescription>
        )}
        {specialData.cta && specialData.cta.url && (
          <SpecialBarCTA
            href={specialData.cta.url}
            target={specialData.cta.target || "_self"}
          >
            {specialData.cta.title || "Learn More"}
          </SpecialBarCTA>
        )}
      </SpecialBarContent>
      <CloseButton onClick={handleClose} aria-label="Close special bar" />
    </SpecialBarWrapper>
  );
};

export default SpecialBar;

